import { router } from "@inertiajs/react";
import { formatCurrency } from "@/lib/utils";
import type { IncentiveLedgerEntry } from "@/types/admin-portal/bonus-ro";
import { parseAmount } from "./rules";

/** The backend's page size when the URL names none. */
export const DEFAULT_LIMIT = 50;

/**
 * The page's URL query with `patch` applied; an undefined or empty value drops the key,
 * so defaults (page 1, the default limit, no search) stay out of the URL.
 */
export function queryWith(
	search: string,
	patch: Record<string, string | number | undefined>,
): Record<string, string> {
	const query = Object.fromEntries(new URLSearchParams(search));
	for (const [key, value] of Object.entries(patch)) {
		if (value === undefined || value === "") delete query[key];
		else query[key] = String(value);
	}
	return query;
}

/**
 * Reloads only `only` with `patch` applied to the URL. The history entry is replaced: the
 * ledger drawer's open state lives in its tab, so a Back press that restored ?ledger=
 * would not reopen it.
 */
export function visit(
	patch: Record<string, string | number | undefined>,
	only: string[],
	onFinish?: () => void,
) {
	router.get(
		window.location.pathname,
		queryWith(window.location.search, patch),
		{
			only,
			preserveState: true,
			preserveScroll: true,
			replace: true,
			onFinish,
		},
	);
}

/** A page number or limit for queryWith: the defaults are left out of the URL. */
export const pageParam = (page: number) => (page > 1 ? page : undefined);
export const limitParam = (limit: number) =>
	limit === DEFAULT_LIMIT ? undefined : limit;

/** A ledger amount with its sign: "+Rp 85.000", "-Rp 5.000". */
export function formatSigned(amount: number): string {
	return `${amount > 0 ? "+" : ""}${formatCurrency(amount)}`;
}

const timeFormat = new Intl.DateTimeFormat("id-ID", {
	timeZone: "Asia/Jakarta",
	dateStyle: "medium",
	timeStyle: "short",
});

/** A backend timestamp shown in WIB, whatever the browser's time zone. */
export const formatTime = (iso: string) => timeFormat.format(new Date(iso));

/** A users.id shown as the admin's name, or a short id when no admin has it; null for none. */
export function actorLabel(
	id: string | null | undefined,
	name: string | null,
): string | null {
	if (!id) return null;
	return name ?? id.slice(0, 8);
}

/** Who wrote a ledger entry; null for an entry the system wrote. */
export const ledgerActor = (entry: IncentiveLedgerEntry) =>
	actorLabel(entry.createdBy, entry.createdByName);

export type AdjustmentDirection = "add" | "subtract";

/** The signed amount an adjustment sends; null unless the text is a whole Rupiah > 0. */
export function adjustmentAmount(
	direction: AdjustmentDirection,
	text: string,
): number | null {
	const amount = parseAmount(text);
	if (!amount) return null;
	return direction === "subtract" ? -amount : amount;
}
